import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { IPeriodicJobs } from '@/modules/periodicJobs/types';
import axiosClient from '@/shared/lib/axios';

interface FertilizationProcess {
  cropId?: number;
  fromDate?: string;
  toDate?: string;
  plotIds?: Array<number>;
  entityId?: number;
  pageNumber: number;
  pageSize: number;
}

// ************************************ Crops *************************************
const getCropsByEntityId = async (entityId?: number) => {
  const { data } = await axiosClient.get<{
    crops: IPeriodicJobs.CropSchema[];
  }>(`/company/crops/${entityId}`);
  return data;
};

export const useCropsByEnityId = (entityId?: number) => {
  return useQuery(
    ['getCropsByEntityId', entityId],
    () => getCropsByEntityId(entityId),
    { enabled: Boolean(entityId) }
  );
};
// ************************************ Crops *************************************

// ************************************ Plots *************************************
const getPlotsByEntityIdAndCropId = async (
  entityId?: number,
  cropId?: number,
  pageNumber = 1,
  pageSize = 10
) => {
  const { data } = await axiosClient.get<{
    plots: IPeriodicJobs.PlotSchema[];
    total: number;
  }>(`/company/crop/plots/${entityId}`, {
    params: { crop: cropId, pageNumber, pageSize },
  });
  return data;
};

export const usePlotsByEnityId = (entityId?: number, cropId?: number) => {
  const pageSize = 10;
  return useInfiniteQuery({
    queryKey: ['getPlotsByEntityIdAndCropId', entityId, cropId],
    queryFn: ({ pageParam = 1 }) =>
      getPlotsByEntityIdAndCropId(entityId, cropId, pageParam, pageSize),
    getNextPageParam: (lastPage, allPags) => {
      const pagesCount = allPags.length;
      const maxPage = Math.ceil(lastPage.total / pageSize);
      return pagesCount < maxPage ? pagesCount + 1 : undefined;
    },
    enabled: Boolean(entityId) && Boolean(cropId),
  });
};
// ************************************ Plots *************************************

// ************************************ Fertilization-Process *************************************
const getFertilizationProcess = async (data: FertilizationProcess) => {
  const { cropId, entityId, fromDate, plotIds, toDate, pageNumber, pageSize } =
    data;

  const params: { [key: string]: string | number } = { pageNumber, pageSize };

  if (toDate) params['toDate'] = toDate;
  if (fromDate) params['fromDate'] = fromDate;
  if (cropId) params['cropId'] = cropId;
  if (plotIds && plotIds.length > 0) params['plotIds'] = plotIds?.join(',');

  const response = await axiosClient.get<{
    jobInstances: IPeriodicJobs.JobInstance[];
    total: number;
  }>(`/company/fertilization-process/${entityId}`, { params });
  return response.data;
};

export const useFertilizationProcess = (data: FertilizationProcess) => {
  const { cropId, entityId, fromDate, plotIds, toDate, pageNumber } = data;
  const plots = plotIds?.join(',');
  return useQuery(
    [
      'getFertilizationProcess',
      cropId,
      entityId,
      fromDate,
      toDate,
      plots,
      pageNumber,
    ],
    () => getFertilizationProcess(data),
    {
      retry: false,
      cacheTime: 0,
      enabled: Boolean(data.cropId) && Boolean(data.entityId),
    }
  );
};
// ************************************ fertilization-process *************************************
